import { HttpRequest } from '@angular/common/http';
import type {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { normalizeDatesToLocalWireStringsDeep } from '@ups/xplat/utils';
import type { Observable } from 'rxjs';

/**
 * Rewrites `Date` instances in POST/PUT/PATCH JSON bodies to **local wall-clock**
 * `YYYY-MM-DDTHH:mm:ss.sss` strings without `Z`, before Angular serializes with
 * `JSON.stringify` / `toISOString()`. Kendo/local pickers encode the user’s local
 * time in the `Date` instant; using UTC getters on the wire would shift that
 * clock by the browser offset (e.g. −2h in UTC+2). See
 * {@link normalizeDatesToLocalWireStringsDeep}. For revived UTC-midnight-only
 * fields that must not pick up local hours, prefer date-only strings at the UI
 * boundary (`toDateInput` / `parseLocalDate`) per frontend-http docs.
 */
@Injectable({
  providedIn: 'root',
})
export class HttpLocalCalendarDateBodyInterceptor implements HttpInterceptor {
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const m = req.method;
    if (m !== 'POST' && m !== 'PUT' && m !== 'PATCH') {
      return next.handle(req);
    }

    const body = req.body;
    if (body == null || typeof body !== 'object') {
      return next.handle(req);
    }

    if (
      body instanceof FormData ||
      body instanceof Blob ||
      body instanceof ArrayBuffer
    ) {
      return next.handle(req);
    }

    return next.handle(
      req.clone({
        body: normalizeDatesToLocalWireStringsDeep(body),
      })
    );
  }
}
